import Grid from "@mui/material/Grid2";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  useFormState,
  useWatch,
} from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { ListOptions } from "../../../models/Master";
import {
  flexAllCentre,
  flexAllEnd,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import { rules } from "../../../utils/validation/Validation";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import {
  TabData,
} from "../../../models/TabSessionStorage";
import { ButtonMinWidth_Outlined, SectionSpacing } from "../styles/style";
import others from "../../../styles/colors/others";
import { RefSourceRadio } from "../../CreatePriorAuth/style/style";
import PhysicianInputForm from "../containers/PhysicianInputForm";
import { NppesType, physicianNames } from "../../../constants/PhysicianDetails";
import { getFormattedPhoneNo, getValue, isNil } from "../../../utils";
import { MemberInfoData } from "../../../models/Patient";
import AddressInputUseForm from "../../../components/FormPriorAuthReAuth/AddressInputUseForm";
import { TypographyBox } from "../../../app/common/TextLabel";
import { updateTabStatus } from "../../../constants/TabSessionStorage";
import { ReferralSourceStateId } from "../../../constants/Referral";
import DatePicker from "../../../components/FormPriorAuthReAuth/DatePicker";

export interface PropsFromState {
  masterReferralSourceState: ListOptions[];
  getValues: UseFormGetValues<TabData>;
  control: Control<any>;
  reset: UseFormReset<TabData>;
  setValue: UseFormSetValue<TabData>;
  memberInfoDataState: MemberInfoData;
  setServiceAddressManualEntry: (value: boolean) => void;
  serviceAddressManualEntry: boolean;
}

export interface PropsFromDispatch {
  saveTabSessionStorage: (SessionData: TabData) => void;
  getMasterReferralSource: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const ReferralSource: React.FC<AllProps> = ({
  control,
  masterReferralSourceState,
  getMasterReferralSource,
  getValues,
  setValue,
  reset,
  memberInfoDataState,
  setServiceAddressManualEntry,
  serviceAddressManualEntry,
}) => {
  const [openReferralSourcePhysician, setOpenReferralSourcePhysician] =
    useState<boolean>(false);
  const [type, setType] = useState<string>(NppesType.PhysicianOffice);
  const [
    openReferralSourcePhysicianAutocomplete,
    setOpenReferralSourcePhysicianAutocomplete,
  ] = useState<boolean>(false);
  const residentialAddress = memberInfoDataState.residentialAddress;
  const mailingAddress = memberInfoDataState.mailingAddress;
  const availableAddress = residentialAddress || mailingAddress;
  const noAddressAvailable = !residentialAddress && !mailingAddress;
  const [manualServiceAddressEntry, setManualServiceAddressEntry] =
    useState<boolean>(false);
  const [localMasterReferralSourceState, setLocalMasterReferralSourceState] = useState<ListOptions[]>([]);

   useEffect(() => {
    const filteredData = masterReferralSourceState.filter(
      (item) => item.value !== ReferralSourceStateId
    );
    setLocalMasterReferralSourceState(filteredData);
  }, [masterReferralSourceState]);

  const selectedFacilityTypeId = useWatch({ control, name: "referralSource.facilityTypeId" });
  const watchedIsDischarged = useWatch({ control, name: "referralSource.IsDischarged" });
  const { errors: referralSourceErrors } = useFormState({
    control,
    name: "referralSource.IsDischarged",
  });
  const dischargedError = (referralSourceErrors as any)?.referralSource?.IsDischarged?.message as string | undefined;
  const selectedSourceLabel =
    localMasterReferralSourceState.find(
      (item) => String(item.value) === String(selectedFacilityTypeId)
    )?.label ?? "";
  const isDischargeSource = /hospital|snf|rehab/i.test(selectedSourceLabel);
  const isPhysicianOfficeSource =
    String(selectedFacilityTypeId) === NppesType.PhysicianOffice;
  const dischargedAnswer: boolean | null = isNil(watchedIsDischarged)
    ? null
    : Boolean(watchedIsDischarged);
  const isExpectedDischarge = isDischargeSource && dischargedAnswer === false;
  const showDateField =
    isPhysicianOfficeSource || (isDischargeSource && dischargedAnswer !== null);

  const getDateFieldConfig = () => {
    if (isExpectedDischarge) {
      return {
        label: "Enter Expected Date of Discharge:",
        helper: rules.expectedDischargeDateRequired,
        allowFutureDate: true,
        minimumDate: dayjs().startOf("day"),
        maximumdate: dayjs().add(29, "day"),
      };
    }
    if (isPhysicianOfficeSource) {
      return {
        label: "Referral Date:",
        helper: rules.referralReceivedDateRequired,
        allowFutureDate: false,
        minimumDate: dayjs().subtract(29, "day").startOf("day"),
        maximumdate: null,
      };
    }
    return {
      label: "Enter Date of Discharge:",
      helper: rules.dischargedDateRequired,
      allowFutureDate: false,
      minimumDate: dayjs().subtract(29, "day").startOf("day"),
      maximumdate: null,
    };
  };
  const dateFieldConfig = getDateFieldConfig();

  const populateResidentialAddress = () => {
    if (!availableAddress) return;
    reset({
      ...getValues(),
      referralSource: {
        ...getValues("referralSource"),
        serviceAddressDetails: {
          ...getValues("referralSource.serviceAddressDetails"),
          streetName1: memberInfoDataState.streetName1,
          streetName2: memberInfoDataState.streetName2 || "",
          city: memberInfoDataState.city || "",
          patientState: memberInfoDataState.patientState || "",
          zipCode: memberInfoDataState.zipCode || "",
          country: memberInfoDataState.country || "",
          county: memberInfoDataState.county || "",
        },
      },
    });
  };

  useEffect(() => {
    getMasterReferralSource();
    if (noAddressAvailable) {
      setServiceAddressManualEntry(true);
      setManualServiceAddressEntry(false);
    }
  }, []);

  useEffect(() => {
    const existing = getValues("referralSource.serviceAddressDetails.streetName1");

    if (serviceAddressManualEntry && !existing && availableAddress) {
      populateResidentialAddress();
    }
  }, []);


  const handleRequestorChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: any) => void
  ) => {
    const selectedValue = event.target.value;

    onChange(selectedValue);
    reset({
      ...getValues(),
      referralSource: {
        ...getValues("referralSource"),
        IsDischarged: null,
        dischargeDate: null,
      },
    });
    reqType();
  };

  const handleDischargedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: any) => void
  ) => {
    onChange(event.target.value === "true");
    reset({
      ...getValues(),
      referralSource: { ...getValues("referralSource"), dischargeDate: null },
    });
  };

  const reqType = () => {
    if (getValues("referralSource.facilityTypeId") === NppesType.PhysicianOffice) {
      setType(NppesType.PhysicianOffice);
    } else {
      setType(NppesType.Others);
    }
  }

  useEffect(() => {
    reqType();
  }, [])

  const physicianFormNames = {
    physicianName: "referralSource.facilityName",
    physicianNpiId: "referralSource.referralSourceNpi",
    phoneNo: "referralSource.referralSourcePhoneNo",
    phoneExt: "referralSource.referralSourceExtension",
    fax: "referralSource.referralSourceFax",
    taxId: "referralSource.referralSourceTaxId",
    address: [
      "referralSource.streetName1",
      "referralSource.streetName2",
      "referralSource.city",
      "referralSource.county",
      "referralSource.referralSourceState",
      "referralSource.zipCode",
    ],
  };

  const handleYesClick = () => {
    setServiceAddressManualEntry(true);
    setManualServiceAddressEntry(false);
    populateResidentialAddress();
  };

  const searchInput = useRef(null);
  const labels = [
    {
      label: "Street 1: ",
      name: "referralSource.serviceAddressDetails.streetName1",
      helper: rules.street,
      textLength: 100,
      grid: 4,
    },
    {
      label: "Street 2: ",
      name: "referralSource.serviceAddressDetails.streetName2",
      textLength: 100,
      grid: 4,
    },
    {
      label: "City: ",
      name: "referralSource.serviceAddressDetails.city",
      textLength: 100,
      helper: rules.city,
      grid: 4,
    },
    {
      label: "State: ",
      name: "referralSource.serviceAddressDetails.patientState",
      helper: rules.state,
      textLength: 100,
      grid: 4,
    },
    {
      label: "County: ",
      name: "referralSource.serviceAddressDetails.county",
      helper: rules.county,
      textLength: 100,
      grid: 4,
    },
    {
      label: "Zip Code: ",
      name: "referralSource.serviceAddressDetails.zipCode",
      helper: rules.zipcode,
      textLength: 10,
      grid: 4,
    },
  ];

  const chooseReferralSourcePhysician = (option: any) => {
    if (!isNil(option)) {
      const {
        name,
        npiId,
        phoneNo,
        phoneExt,
        county,
        streetName1,
        streetName2,
        fax,
        city,
        state,
        zipCode,
      } = option;

      const getFacilityTypeName = localMasterReferralSourceState.find(
        (item) =>
          item.value === Number(getValues("referralSource.facilityTypeId"))
      );
      setValue(
        "referralSource.facilityTypeName",
        getFacilityTypeName?.label || ""
      );
      reset({
        ...getValues(),
        referralSource: {
          ...getValues("referralSource"),
          facilityName: name,
          referralSourceNpi: npiId,
          streetName1: streetName1,
          streetName2: streetName2,
          city: city,
          county: county,
          zipCode: zipCode,
          referralSourceFax: getFormattedPhoneNo(fax),
          referralSourceState: state,
          referralSourceExtension: phoneExt,
          referralSourcePhoneNo: phoneNo ? getFormattedPhoneNo(phoneNo) : "",
        },
      });
    }
  };

  useEffect(() => {
    reset({
      ...getValues(),
      tabStatus: updateTabStatus(1, getValues("tabStatus")),
    });
  }, []);

  const clearReferralSourcePhysicianDetails = () => {
    reset({
      ...getValues(),
      referralSource: {
        ...getValues("referralSource"),
        facilityName: "",
        referralSourceNpi: "",
        referralSourceName: "",
        referralSourcePhoneNo: "",
        referralSourceFax: "",
        streetName1: "",
        streetName2: "",
        county: "",
        city: "",
        referralSourceState: "",
        zipCode: "",
        referralSourceExtension: "",
        referralSourceEmail: "",
        referralSourceTaxId: "",
      },
    },
    );
  };

  return (
    <>
      <Grid size={12}>
        <Grid container>
          <Grid size={12} sx={flexColumn} mt={2}>
            <Grid size={{ xs: 12 }} sx={SectionSpacing}>
              <Box>
                {TypographyBox(
                  "REFERRAL SOURCE",
                  colors.fonts[20],
                  fontWeight[5]
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }} sx={SectionSpacing}>
              <Controller
                defaultValue=""
                render={({ field, fieldState }) => (
                  <Box>
                    <>
                      <RadioGroup
                        aria-labelledby="controlled-radio-buttons-group"
                        sx={RefSourceRadio(false)}
                        {...field}
                        onChange={(e) =>
                          handleRequestorChange(e, field.onChange)
                        }
                      >
                        {localMasterReferralSourceState &&
                          localMasterReferralSourceState.length > 0 &&
                          localMasterReferralSourceState.map(
                            (singleOption: ListOptions, index: number) => (
                              <Box key={singleOption.value} pr={"1rem"}>
                                <FormControlLabel
                                  value={Number(singleOption.value)}
                                  label={
                                    <Box sx={flexAllCentre}>
                                      <Typography
                                        variant="h6"
                                        color={others.otherColors[63]}
                                        fontWeight={fontWeight[3]}
                                      >
                                        {singleOption.label}
                                      </Typography>
                                      {localMasterReferralSourceState.length - 1 ===
                                        index &&
                                        !getValue(
                                          rules,
                                          "required.value",
                                          ""
                                        ) && (
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight={fontWeight[2]}
                                            color={colors.red[103]}
                                            pl={"0.4rem"}
                                          >
                                            *
                                          </Typography>
                                        )}
                                    </Box>
                                  }
                                  control={<Radio />}
                                />
                              </Box>
                            )
                          )}
                      </RadioGroup>
                      {fieldState.error && (
                        <Typography
                          variant="subtitle1"
                          fontWeight={fontWeight[4]}
                          color={colors.red[100]}
                          sx={{ mt: 1, ml: 2 }}
                        >
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </>
                  </Box>
                )}
                name={"referralSource.facilityTypeId"}
                control={control}
                rules={rules.requestorRequired}
              />
            </Grid>
            {(isDischargeSource || showDateField) && (
              <Grid size={{ xs: 12 }} sx={SectionSpacing}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: isDischargeSource ? "flex-start" : "center",
                  }}
                >
                  {isPhysicianOfficeSource && (
                    <Box sx={flexRow} pr={"1.5rem"}>
                      <Typography
                        variant="h6"
                        fontWeight={fontWeight[5]}
                        color={colors.fonts[20]}
                      >
                        When did you receive this referral?
                      </Typography>
                    </Box>
                  )}
                  {isDischargeSource && (
                    <Box pr={"1.5rem"}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={flexRow}>
                          <Typography
                            variant="h6"
                            fontWeight={fontWeight[5]}
                            color={colors.fonts[20]}
                          >
                            Has this member been discharged?
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            fontWeight={fontWeight[2]}
                            color={colors.red[103]}
                            pl={"0.3rem"}
                          >
                            *
                          </Typography>
                        </Box>
                        <Controller
                          name="referralSource.IsDischarged"
                          control={control}
                          rules={rules.isDischargedRequired}
                          render={({ field }) => (
                            <RadioGroup
                              row
                              aria-label="Has this member been discharged?"
                              sx={{ pl: "1rem" }}
                              value={isNil(field.value) ? "" : String(field.value)}
                              onChange={(e) =>
                                handleDischargedChange(e, field.onChange)
                              }
                            >
                              <FormControlLabel
                                value="true"
                                label="Yes"
                                sx={{ fontSize: "0.81rem", fontWeight: fontWeight[3] }}
                                control={<Radio />}
                              />
                              <FormControlLabel
                                value="false"
                                label="No"
                                sx={{ fontSize: "0.81rem", fontWeight: fontWeight[3] }}
                                control={<Radio />}
                              />
                            </RadioGroup>
                          )}
                        />
                      </Box>
                      {dischargedError && (
                        <Typography
                          variant="subtitle1"
                          fontWeight={fontWeight[2]}
                          color={colors.red[100]}
                        >
                          {dischargedError}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {showDateField && (isDischargeSource || isPhysicianOfficeSource) && (
                    <Divider orientation="vertical" flexItem />
                  )}
                  {showDateField && (
                    <Box pl={"1.5rem"}>
                      <FormControl variant="standard">
                        <DatePicker
                          key={`${isExpectedDischarge}-${isPhysicianOfficeSource}`}
                          name="referralSource.dischargeDate"
                          control={control}
                          label={dateFieldConfig.label}
                          helper={dateFieldConfig.helper}
                          allowFutureDate={dateFieldConfig.allowFutureDate}
                          minimumDate={dateFieldConfig.minimumDate}
                          maximumdate={dateFieldConfig.maximumdate}
                          InputStyle={{ minWidth: "18rem" }}
                        />
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
            <Grid size={{ xs: 12 }} sx={SectionSpacing}>
              <PhysicianInputForm
                control={control}
                getValues={getValues}
                setValue={setValue}
                reset={reset}
                names={physicianFormNames}
                open={openReferralSourcePhysician}
                setOpen={setOpenReferralSourcePhysician}
                openAutocomplete={openReferralSourcePhysicianAutocomplete}
                setOpenAutocomplete={setOpenReferralSourcePhysicianAutocomplete}
                physicianFormName={physicianNames.referralSourcePhysician}
                noValidate={false}
                choosePhysician={chooseReferralSourcePhysician}
                type={type}
                isSourceName={true}
                renderClearButton={clearReferralSourcePhysicianDetails}
              />
            </Grid>
            <Divider />
            <Grid container spacing={2} p={"2rem 4rem 1rem 2rem"} sx={flexRow}>
              <Grid size={{ xs: 7 }}>
                <Box>
                  {TypographyBox(
                    "Service Address",
                    colors.fonts[20],
                    fontWeight[5]
                  )}
                </Box>
                {!noAddressAvailable ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      fontWeight={fontWeight[3]}
                      color={colors.fonts[2]}
                      style={{ paddingTop: "0.3rem" }}
                    >
                      Is this the address where the member will be receiving
                      services?
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={fontWeight[3]}
                      style={{ paddingTop: "0.5rem" }}
                    >
                      {availableAddress}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle1"
                      fontWeight={fontWeight[3]}
                      color={colors.fonts[2]}
                      style={{ paddingTop: "0.3rem" }}
                    >
                      Please enter the service address for the member
                    </Typography>
                  </>
                )}
              </Grid>
              {!noAddressAvailable && (
                <Grid size={{ xs: 5 }} sx={flexAllEnd}>
                  <Button
                    variant="outlined"
                    onClick={handleYesClick}
                    sx={ButtonMinWidth_Outlined}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setManualServiceAddressEntry(true);
                      setServiceAddressManualEntry(false)
                    }}
                    sx={ButtonMinWidth_Outlined}
                  >
                    No
                  </Button>
                </Grid>
              )}
            </Grid>
            {manualServiceAddressEntry && (
              <>
                <Grid size={{ xs: 12 }} p={"0rem 2rem 1rem 2rem"}>
                  <AddressInputUseForm
                    control={control}
                    searchInput={searchInput}
                    reset={reset}
                    getValues={getValues}
                    setValue={setValue}
                    labels={labels}
                    addressLabel=""
                    addressVariant="subtitle1"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ReferralSource;
